import api from './api'

/**
 * Initiate a GIS vector upload — server creates the layer (status='ingesting')
 * and returns presigned URLs (single or multipart). Lot 1f, gabarit gisRasterApi.
 * @param {Object} payload — { filename, layer_name, description?, folder_id?, visible_to_users?, visible_to_groups?, size, mime }
 * @returns {Promise<{ layer_id: number, mode: 'single'|'multipart', put_url?: string, multipart_upload_id?: string, chunk_size?: number, total_chunks?: number, presigned_part_urls?: Array<{part_number: number, url: string}>, expires_in: number }>}
 */
export async function initiateVectorUpload(payload) {
  const res = await api.post('/gis/vector/initiate', payload)
  return res.data
}

/**
 * Finalize a GIS vector upload — completes multipart on S3 (if applicable) and kicks Django.
 * @param {number|string} layerId
 * @param {{mode: 'single'} | {mode: 'multipart', parts: Array<{part_number: number, etag: string}>}} finalizeBody
 * @returns {Promise<{ status: string, layer_id: number }>}
 */
export async function finalizeVectorUpload(layerId, finalizeBody) {
  const res = await api.post(`/gis/vector/finalize/${layerId}`, finalizeBody)
  return res.data
}

/**
 * Abort a GIS vector upload — releases S3 multipart and deletes the layer row.
 * @param {number|string} layerId
 * @returns {Promise<{ success: boolean }>}
 */
export async function abortVectorUpload(layerId) {
  const res = await api.post(`/gis/vector/abort/${layerId}`)
  return res.data
}

/**
 * List in-flight / recent GIS vector imports for the JWT user's resort.
 * Server scopes by user.resort_id.
 * @returns {Promise<{ layers: Array }>}
 */
export async function listVectorImports() {
  const res = await api.get('/gis/vector')
  return res.data
}

/**
 * Request an async server-side export of an operational layer to a vector format.
 * Triggers ogr2ogr via Django ; completion arrives over Socket.io 'gis-vector-export-progress'.
 * @param {number|string} layerId
 * @param {'kml'|'geojson'|'gpkg'|'shp'|'gpx'|'dxf'} format
 * @returns {Promise<{ export_id: string, status: string }>}
 */
export async function requestLayerExport(layerId, format) {
  const res = await api.post(`/operational-layers/${layerId}/export`, { format })
  return res.data
}

/**
 * Request an async multi-layer ZONE export (features intersecting a drawn polygon) → one ZIP.
 * @param {Object} zone - GeoJSON geometry (Polygon|MultiPolygon)
 * @param {Array<number|string>} layerIds
 * @param {'kml'|'geojson'|'gpkg'|'shp'|'gpx'|'dxf'} format
 * @returns {Promise<{ export_id: string, status: string, included: number, skipped: number }>}
 */
export async function requestZoneExport(zone, layerIds, format) {
  const res = await api.post('/operational-layers/export-zone', { zone, layer_ids: layerIds, format })
  return res.data
}

/**
 * Preview which migrated layers have features intersecting a drawn zone (with counts).
 * ACL-scoped server-side. Feeds the zone export panel.
 * @param {Object} zone - GeoJSON geometry (Polygon|MultiPolygon)
 * @returns {Promise<{ layers: Array<{ layer_id: number, name: string, count: number }> }>}
 */
export async function previewZoneExport(zone) {
  const res = await api.post('/operational-layers/zone-preview', { zone })
  return res.data
}

/**
 * Fetch the features INSIDE a drawn zone for the given layers, combined into one
 * FeatureCollection — for the preview highlight (not the export). ACL-scoped.
 * @param {Object} zone - GeoJSON geometry (Polygon|MultiPolygon)
 * @param {Array<number|string>} layerIds
 * @returns {Promise<{ geojson: object, truncated: boolean, count: number }>}
 */
export async function zoneFeatures(zone, layerIds) {
  const res = await api.post('/operational-layers/zone-features', { zone, layer_ids: layerIds })
  return res.data
}
